/**
 * ダッシュボード用のシンプル集計 hook。
 * collectionGroup で messages を集計するため Firestore のインデックスが必要。
 */
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  collectionGroup,
  getCountFromServer,
  getDocs,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';

export interface DashboardMetrics {
  line: {
    todayConversations: number;
    lowRatingUnreviewed: number;
    activeUsers30d: number;
    avgRating7d: number;
  };
  mail: {
    generatedThisMonth: number;
    sentThisMonth: number;
    pendingReview: number;
    drafts: number;
  };
}

export function useDashboardMetrics() {
  return useQuery({
    queryKey: ['dashboard_metrics'],
    staleTime: 60_000,
    queryFn: async (): Promise<DashboardMetrics> => {
      const now = new Date();
      const startOfToday = new Date(now);
      startOfToday.setHours(0, 0, 0, 0);
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const todayTs = Timestamp.fromDate(startOfToday);
      const monthTs = Timestamp.fromDate(startOfMonth);
      const thirtyTs = Timestamp.fromDate(thirtyDaysAgo);
      const sevenTs = Timestamp.fromDate(sevenDaysAgo);

      // LINE: 今日の会話数（assistant メッセージ件数）
      const todayMsgCount = await getCountFromServer(
        query(
          collectionGroup(db, 'messages'),
          where('role', '==', 'assistant'),
          where('timestamp', '>=', todayTs),
        ),
      ).catch(() => ({ data: () => ({ count: 0 }) }));

      // LINE: 低評価未レビュー
      const lowRatingSnap = await getDocs(
        query(
          collection(db, 'qa_history'),
          where('rating', '<=', 2),
          where('adminReviewed', '==', false),
        ),
      ).catch(() => ({ size: 0, docs: [] as never[] }));

      // LINE: アクティブユーザー（30 日以内に lastMessageAt）
      const activeSnap = await getDocs(
        query(collection(db, 'users'), where('lastMessageAt', '>=', thirtyTs)),
      ).catch(() => ({ size: 0 }));

      // LINE: 過去 7 日の平均評価
      const ratingSnap = await getDocs(
        query(collection(db, 'qa_history'), where('createdAt', '>=', sevenTs)),
      ).catch(() => ({ docs: [] as never[] }));
      const ratings: number[] = [];
      for (const d of ratingSnap.docs) {
        const r = (d as { data: () => { rating?: number } }).data().rating;
        if (typeof r === 'number') ratings.push(r);
      }
      const avgRating7d =
        ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;

      // Mail: 今月の生成数
      const mailMonthSnap = await getCountFromServer(
        query(collection(db, 'mails'), where('createdAt', '>=', monthTs)),
      ).catch(() => ({ data: () => ({ count: 0 }) }));

      // Mail: 今月の送信数
      const sentMonthSnap = await getCountFromServer(
        query(
          collection(db, 'mails'),
          where('sentAt', '>=', monthTs),
          where('status', '==', 'sent'),
        ),
      ).catch(() => ({ data: () => ({ count: 0 }) }));

      // Mail: 承認待ち
      const reviewSnap = await getCountFromServer(
        query(
          collection(db, 'mails'),
          where('status', '==', 'review'),
          where('_deleted', '==', false),
        ),
      ).catch(() => ({ data: () => ({ count: 0 }) }));

      // Mail: 下書き
      const draftSnap = await getCountFromServer(
        query(
          collection(db, 'mails'),
          where('status', '==', 'draft'),
          where('_deleted', '==', false),
        ),
      ).catch(() => ({ data: () => ({ count: 0 }) }));

      return {
        line: {
          todayConversations: todayMsgCount.data().count ?? 0,
          lowRatingUnreviewed: lowRatingSnap.size ?? 0,
          activeUsers30d: activeSnap.size ?? 0,
          avgRating7d: Number(avgRating7d.toFixed(2)),
        },
        mail: {
          generatedThisMonth: mailMonthSnap.data().count ?? 0,
          sentThisMonth: sentMonthSnap.data().count ?? 0,
          pendingReview: reviewSnap.data().count ?? 0,
          drafts: draftSnap.data().count ?? 0,
        },
      };
    },
  });
}
